import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";

const COPY = {
  en: {
    hero_title: "About The Archive",
    hero_sub:
      "A curated digital library for engineering, extractive metallurgy, mining, and scientific research.",

    mission_h: "Our mission",
    mission_p1:
      "The Archive is a project of 2RA-Edition — a small independent editorial team based in the Democratic Republic of the Congo, at the heart of one of the world's most active mining regions. Our mission is to make high-quality technical literature accessible to engineers, researchers, students, and industry professionals across the globe, with a particular focus on extractive metallurgy, hydrometallurgy, mining engineering, industrial chemistry, and applied scientific research.",
    mission_p2:
      "We believe that access to reliable technical knowledge should not be gated by geography, institutional affiliation, or paywalls that price out entire regions. Every document uploaded to The Archive is indexed with full-text search, enriched with AI-generated metadata, and made available with abstracts translated into ten languages — so a Congolese metallurgy student in Lubumbashi has the same reading experience as a professor in Freiberg or a process engineer in Antofagasta.",

    what_h: "What you can do here",
    what_i1_h: "Search across every page",
    what_i1_p:
      "Full-text search that looks inside the PDF content itself — not just titles and abstracts. Combine it with faceted filters on Smart Search to narrow by year, language, category, organization, and author.",
    what_i2_h: "Read with AI assistance",
    what_i2_p:
      "Every document gets an AI-generated abstract, machine-extracted metadata (title, authors, year, keywords, language), and on-demand translation of the abstract into English, French, Spanish, German, Portuguese, Italian, Russian, Chinese, Japanese, or Arabic.",
    what_i3_h: "Build reading collections",
    what_i3_p:
      "Group documents into public or private collections — a course syllabus, a bibliography for a thesis, a project reference list — and share them with the community or keep them for yourself.",
    what_i4_h: "Submit your own work",
    what_i4_p:
      "Independent authors, research groups, and industry practitioners can submit their own manuscripts through our submission portal. Approved documents earn a share of every paid download via Stripe Connect.",

    values_h: "How we work",
    values_i1:
      "Documents are converted to standardized PDFs at upload time, indexed for search, and checked for duplicates against the entire library.",
    values_i2:
      "Scanned or image-only PDFs are OCR'd in the background so their text is searchable within minutes of upload.",
    values_i3:
      "A near-duplicate detector prevents the library from cluttering with slight variations of the same paper, and gives admins a diagnostic view to resolve conflicts.",
    values_i4:
      "Personal data is handled per our terms of service; downloads and account activity are logged for security and audit purposes only.",

    contact_h: "Get in touch",
    contact_p:
      "For inquiries about content, licensing, partnerships, or if you would like to submit a large collection of documents, email us at",
    contact_email: "info@2ra-edition.com",

    cta_search: "Search the library",
    cta_subscribe: "See subscription plans",
    back: "Back",
  },
  fr: {
    hero_title: "À propos des Archives",
    hero_sub:
      "Une bibliothèque numérique dédiée à l'ingénierie, à la métallurgie extractive, aux mines et à la recherche scientifique.",

    mission_h: "Notre mission",
    mission_p1:
      "Les Archives est un projet porté par 2RA-Edition, une équipe éditoriale indépendante basée en République Démocratique du Congo, au cœur de l'une des régions minières les plus actives au monde. Notre mission est de rendre la littérature technique de haut niveau accessible aux ingénieurs, chercheurs, étudiants et professionnels de l'industrie du monde entier, avec un intérêt particulier pour la métallurgie extractive, l'hydrométallurgie, l'ingénierie minière, la chimie industrielle et la recherche scientifique appliquée.",
    mission_p2:
      "Nous pensons que l'accès à une connaissance technique fiable ne doit pas dépendre de la géographie, de l'affiliation institutionnelle, ni de barrières tarifaires qui excluent des régions entières. Chaque document déposé aux Archives est indexé en texte intégral, enrichi de métadonnées générées par IA, et proposé avec des résumés traduisibles en dix langues — pour qu'un étudiant en métallurgie à Lubumbashi ait la même expérience de lecture qu'un professeur à Freiberg ou un ingénieur procédé à Antofagasta.",

    what_h: "Ce que vous pouvez faire",
    what_i1_h: "Rechercher chaque page",
    what_i1_p:
      "Recherche plein-texte au sein même du contenu des PDF — pas seulement les titres et résumés. Combinez-la avec les filtres avancés de la Recherche Intelligente pour affiner par année, langue, catégorie, organisation et auteur.",
    what_i2_h: "Lire avec l'IA",
    what_i2_p:
      "Chaque document dispose d'un résumé généré par IA, de métadonnées extraites automatiquement (titre, auteurs, année, mots-clés, langue), et de traductions à la demande vers l'anglais, le français, l'espagnol, l'allemand, le portugais, l'italien, le russe, le chinois, le japonais ou l'arabe.",
    what_i3_h: "Créer des collections",
    what_i3_p:
      "Regroupez des documents dans des collections publiques ou privées — bibliographie de thèse, syllabus de cours, dossier projet — et partagez-les avec la communauté ou gardez-les pour vous.",
    what_i4_h: "Soumettre vos travaux",
    what_i4_p:
      "Auteurs indépendants, laboratoires et praticiens peuvent soumettre leurs propres manuscrits via notre portail de soumission. Les documents acceptés perçoivent une part de chaque téléchargement payant via Stripe Connect.",

    values_h: "Comment nous travaillons",
    values_i1:
      "Les documents sont convertis en PDF standardisés lors de l'upload, indexés pour la recherche et confrontés à la base pour éviter les doublons.",
    values_i2:
      "Les PDF numérisés ou uniquement images sont océrisés en arrière-plan afin que leur texte soit consultable en quelques minutes.",
    values_i3:
      "Un détecteur de quasi-doublons empêche l'accumulation de variantes du même document, tout en offrant aux administrateurs une vue diagnostique pour arbitrer les conflits.",
    values_i4:
      "Les données personnelles sont traitées conformément à nos conditions d'utilisation ; téléchargements et activité de compte sont journalisés à des fins de sécurité et d'audit uniquement.",

    contact_h: "Nous contacter",
    contact_p:
      "Pour toute question sur les contenus, les licences, les partenariats, ou pour proposer un fonds documentaire important, écrivez-nous à",
    contact_email: "info@2ra-edition.com",

    cta_search: "Explorer la bibliothèque",
    cta_subscribe: "Voir les abonnements",
    back: "Retour",
  },
};

const SERIF = { fontFamily: "'Fraunces', 'Cormorant Garamond', serif" };

const AboutPage = ({ user }) => {
  const navigate = useNavigate();
  const [lang, setLang] = useState(
    () => localStorage.getItem("archive_lang") || "en",
  );
  const t = COPY[lang];

  return (
    <div className="min-h-screen bg-sky-50">
      <Navigation user={user} />
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-14">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-slate-500 hover:text-slate-800 mb-8 inline-flex items-center gap-1"
          data-testid="about-back-btn"
        >
          ← {t.back}
        </button>

        {/* Language toggle */}
        <div className="flex gap-2 mb-6 text-xs" data-testid="about-lang-toggle">
          <button
            onClick={() => {
              setLang("en");
              localStorage.setItem("archive_lang", "en");
            }}
            className={`uppercase tracking-widest ${lang === "en" ? "text-slate-900 font-semibold" : "text-slate-400 hover:text-slate-700"}`}
          >
            EN
          </button>
          <span className="text-slate-300">/</span>
          <button
            onClick={() => {
              setLang("fr");
              localStorage.setItem("archive_lang", "fr");
            }}
            className={`uppercase tracking-widest ${lang === "fr" ? "text-slate-900 font-semibold" : "text-slate-400 hover:text-slate-700"}`}
          >
            FR
          </button>
        </div>

        <h1
          className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4 tracking-tight"
          style={SERIF}
          data-testid="about-title"
        >
          {t.hero_title}
        </h1>
        <p className="text-lg text-slate-600 mb-12 max-w-2xl leading-relaxed">
          {t.hero_sub}
        </p>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={SERIF}>
            {t.mission_h}
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4">{t.mission_p1}</p>
          <p className="text-slate-700 leading-relaxed">{t.mission_p2}</p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-slate-900 mb-6" style={SERIF}>
            {t.what_h}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              [t.what_i1_h, t.what_i1_p],
              [t.what_i2_h, t.what_i2_p],
              [t.what_i3_h, t.what_i3_p],
              [t.what_i4_h, t.what_i4_p],
            ].map(([h, p], i) => (
              <div
                key={i}
                className="bg-white/70 border border-slate-200 rounded-lg p-5"
                data-testid={`about-feature-${i}`}
              >
                <h3 className="text-base font-semibold text-slate-900 mb-2">{h}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={SERIF}>
            {t.values_h}
          </h2>
          <ul className="list-disc list-inside space-y-3 text-slate-700 leading-relaxed">
            <li>{t.values_i1}</li>
            <li>{t.values_i2}</li>
            <li>{t.values_i3}</li>
            <li>{t.values_i4}</li>
          </ul>
        </section>

        <section className="mb-16 p-6 rounded-lg bg-white/70 border border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900 mb-3" style={SERIF}>
            {t.contact_h}
          </h2>
          <p className="text-slate-700 leading-relaxed">
            {t.contact_p}{" "}
            <a
              href={`mailto:${t.contact_email}`}
              className="text-orange-600 hover:underline font-medium"
              data-testid="about-contact-email"
            >
              {t.contact_email}
            </a>
            .
          </p>
        </section>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate("/search")}
            className="px-6 py-3 rounded-full text-white bg-orange-600 hover:bg-orange-700 text-sm font-medium"
            data-testid="about-cta-search"
          >
            {t.cta_search}
          </button>
          <button
            onClick={() => navigate("/subscriptions")}
            className="px-6 py-3 rounded-full border border-slate-300 text-slate-800 hover:bg-slate-100 text-sm font-medium"
            data-testid="about-cta-subscribe"
          >
            {t.cta_subscribe}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
